import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { formatTimePart, getNearestAvailableTime } from 'helpers/timeUtils';
import styles from 'components/TimePicker/TimePicker.scss';

export const TimePicker = ({
  hoursValue = null,
  minutesValue = null,
  setTime,
  name,
  hoursError,
  minutesError,
  setMinutesError,
  setHoursError,
  timeLimits = {
    minHours: 0,
    maxHours: 23,
    minMinutes: 0,
    maxMinutes: 59,
  },
  initialTime,
}) => {
  const parseInitialTime = useMemo(() => {
    if (!initialTime) return { hours: timeLimits.minHours, minutes: timeLimits.minMinutes };
    try {
      const [h, m] = initialTime.split(':').map(Number);
      return { hours: h, minutes: m };
    } catch {
      return { hours: timeLimits.minHours, minutes: timeLimits.minMinutes };
    }
  }, [initialTime, timeLimits]);

  const initialHours = useMemo(() => hoursValue || formatTimePart(parseInitialTime.hours), []);
  const initialMinutes = useMemo(() => minutesValue || formatTimePart(parseInitialTime.minutes), []);

  const [hours, setHours] = useState(initialHours);
  const [minutes, setMinutes] = useState(initialMinutes);

  useEffect(() => {
    if (hoursValue) setHours(hoursValue);
    if (minutesValue) setMinutes(minutesValue);
  }, [hoursValue, minutesValue]);

  const validateTime = useCallback(
    (h, m) => {
      const hourNum = Number(h);
      const minuteNum = Number(m);
      if (Number.isNaN(hourNum) || Number.isNaN(minuteNum)) return false;
      if (h.length !== 2 || m.length !== 2) return false;
      if (hourNum < timeLimits.minHours || hourNum > timeLimits.maxHours) return false;
      if (minuteNum < 0 || minuteNum > 59) return false;
      if (hourNum === timeLimits.minHours && minuteNum < timeLimits.minMinutes) return false;
      if (hourNum === timeLimits.maxHours && minuteNum > timeLimits.maxMinutes) return false;
      return true;
    },
    [timeLimits]
  );

  const updateParentTimeExact = useCallback(
    (h, m) => {
      if (!validateTime(h, m)) return;
      const timeValue = `${h}:${m}`;
      if (name) setTime({ target: { name, value: timeValue } });
      else setTime(timeValue);
    },
    [validateTime, name, setTime]
  );

  const updateParentTimeNearest = useCallback(
    (h, m) => {
      const nearest = getNearestAvailableTime(`${h}:${m}`, timeLimits);
      const timeValue = `${formatTimePart(nearest.hours)}:${formatTimePart(nearest.minutes)}`;
      if (name) setTime({ target: { name, value: timeValue } });
      else setTime(timeValue);
    },
    [timeLimits, name, setTime]
  );

  useEffect(() => {
    const hourNum = Number(hours);
    const minuteNum = Number(minutes);

    if (hours.length === 2) {
      if (hourNum < timeLimits.minHours || hourNum > timeLimits.maxHours) {
        setHoursError((prev) => ({
          ...prev,
          [name]: `Значення годин повинно бути у діапазоні ${timeLimits.minHours}-${timeLimits.maxHours}`,
        }));
      } else {
        setHoursError((prev) => ({ ...prev, [name]: '' }));
      }
    }

    if (minutes.length === 2) {
      const isEndTime = timeLimits.maxHours === 22;
      const minMinutesForHour = isEndTime && hourNum === 9 ? 15 : 0;
      const maxMinutesForHour = isEndTime && hourNum === 22 ? 0 : !isEndTime && hourNum === 21 ? 45 : 59;

      if (minuteNum < 0 || minuteNum > 59) {
        setMinutesError((prev) => ({
          ...prev,
          [name]: `Значення хвилин повинно бути у діапазоні 00-59`,
        }));
      } else if (
        (hourNum === timeLimits.minHours && minuteNum < minMinutesForHour) ||
        (hourNum === timeLimits.maxHours && minuteNum > maxMinutesForHour)
      ) {
        setMinutesError((prev) => ({
          ...prev,
          [name]: `Неприпустимий час для обраної години`,
        }));
      } else {
        setMinutesError((prev) => ({ ...prev, [name]: '' }));
      }
    }
  }, [hours, minutes, name, setHoursError, setMinutesError]);

  const incrementHours = useCallback(() => {
    const curH = Number(hours);
    const curM = Number(minutes);
    const isEndTime = timeLimits.maxHours === 22;

    let nextH = curH + 1;
    let nextM = curM;

    if (nextH > timeLimits.maxHours) {
      const minM = isEndTime && timeLimits.minHours === 9 ? 15 : timeLimits.minMinutes;
      nextH = timeLimits.minHours;
      nextM = minM;
    } else {
      if (nextH === timeLimits.maxHours) {
        const maxM = isEndTime ? 0 : 45;
        if (nextM > maxM) nextM = maxM;
      }
      if (nextH === timeLimits.minHours) {
        const minM = isEndTime && nextH === 9 ? 15 : timeLimits.minMinutes;
        if (nextM < minM) nextM = minM;
      }
    }

    const outH = formatTimePart(nextH);
    const outM = formatTimePart(nextM - (nextM % 15));
    setHours(outH);
    setMinutes(outM);
    updateParentTimeNearest(outH, outM);
  }, [hours, minutes, timeLimits, updateParentTimeNearest]);

  const decrementHours = useCallback(() => {
    const curH = Number(hours);
    const curM = Number(minutes);
    const isEndTime = timeLimits.maxHours === 22;

    let nextH = curH - 1;
    let nextM = curM;

    if (nextH < timeLimits.minHours) {
      const maxM = isEndTime ? 0 : 45;
      nextH = timeLimits.maxHours;
      nextM = maxM;
    } else {
      if (nextH === timeLimits.maxHours) {
        const maxM = isEndTime ? 0 : 45;
        if (nextM > maxM) nextM = maxM;
      }
      if (nextH === timeLimits.minHours) {
        const minM = isEndTime && nextH === 9 ? 15 : timeLimits.minMinutes;
        if (nextM < minM) nextM = minM;
      }
    }

    const outH = formatTimePart(nextH);
    const outM = formatTimePart(nextM - (nextM % 15));
    setHours(outH);
    setMinutes(outM);
    updateParentTimeNearest(outH, outM);
  }, [hours, minutes, timeLimits, updateParentTimeNearest]);

  const incrementMinutes = useCallback(() => {
    const curH = Number(hours);
    const curM = Number(minutes);
    const isEndTime = timeLimits.maxHours === 22;

    let nextH = curH;
    let nextM = Math.ceil((curM + 1) / 15) * 15;

    if (nextM >= 60) {
      nextM = 0;
      if (curH >= timeLimits.maxHours) {
        nextH = timeLimits.minHours;
        nextM = isEndTime && timeLimits.minHours === 9 ? 15 : timeLimits.minMinutes;
      } else {
        nextH = curH + 1;
      }
    }

    const maxMForHour = isEndTime && nextH === 22 ? 0 : !isEndTime && nextH === 21 ? 45 : 59;
    if (nextH === timeLimits.maxHours && nextM > maxMForHour) {
      nextH = timeLimits.minHours;
      nextM = isEndTime && timeLimits.minHours === 9 ? 15 : timeLimits.minMinutes;
    }

    const outH = formatTimePart(nextH);
    const outM = formatTimePart(nextM);
    setHours(outH);
    setMinutes(outM);
    updateParentTimeNearest(outH, outM);
  }, [hours, minutes, timeLimits, updateParentTimeNearest]);

  const decrementMinutes = useCallback(() => {
    const curH = Number(hours);
    const curM = Number(minutes);
    const isEndTime = timeLimits.maxHours === 22;

    let nextH = curH;
    let nextM = Math.floor((curM - 1) / 15) * 15;

    if (nextM < 0) {
      nextM = 45;
      if (curH <= timeLimits.minHours) {
        nextH = timeLimits.maxHours;
        nextM = isEndTime ? 0 : 45;
      } else {
        nextH = curH - 1;
      }
    }

    const minMForHour = isEndTime && nextH === 9 ? 15 : timeLimits.minMinutes;
    if (nextH === timeLimits.minHours && nextM < minMForHour) {
      nextH = timeLimits.maxHours;
      nextM = isEndTime ? 0 : 45;
    }

    const outH = formatTimePart(nextH);
    const outM = formatTimePart(nextM);
    setHours(outH);
    setMinutes(outM);
    updateParentTimeNearest(outH, outM);
  }, [hours, minutes, timeLimits, updateParentTimeNearest]);

  const handleHoursChange = useCallback((e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    setHours(v);
  }, []);

  const handleMinutesChange = useCallback((e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMinutes(v);
  }, []);

  const handleHoursBlur = useCallback(() => {
    const num = Number(hours);
    if (Number.isNaN(num)) {
      const outH = formatTimePart(timeLimits.minHours);
      setHours(outH);
      updateParentTimeExact(outH, formatTimePart(minutes));
      return;
    }

    const clamped = Math.max(timeLimits.minHours, Math.min(timeLimits.maxHours, num));
    const outH = formatTimePart(clamped);

    setHours(outH);
    updateParentTimeExact(outH, formatTimePart(minutes));
  }, [hours, minutes, timeLimits, updateParentTimeExact]);

  const handleMinutesBlur = useCallback(() => {
    const num = Number(minutes);

    if (Number.isNaN(num)) {
      const outM = formatTimePart(timeLimits.minMinutes);
      setMinutes(outM);
      updateParentTimeExact(formatTimePart(hours), outM);
      return;
    }

    const clamped = Math.max(0, Math.min(59, num));
    const outM = formatTimePart(clamped);

    setMinutes(outM);
    updateParentTimeExact(formatTimePart(hours), outM);
  }, [minutes, hours, timeLimits, updateParentTimeExact]);

  return (
    <div className={styles.timePicker}>
      <div className={styles.timePart}>
        <button onClick={decrementHours} type='button'>
          <FontAwesomeIcon icon={faAngleUp} />
        </button>
        <input
          className={cn({ [styles.invalid]: hoursError?.[name] })}
          value={hours}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          data-name='custom'
          maxLength={2}
        />
        <button onClick={incrementHours} type='button'>
          <FontAwesomeIcon icon={faAngleDown} />
        </button>
      </div>
      :
      <div className={styles.timePart}>
        <button onClick={decrementMinutes} type='button'>
          <FontAwesomeIcon icon={faAngleUp} />
        </button>
        <input
          className={cn({ [styles.invalid]: minutesError?.[name] })}
          value={minutes}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          data-name='custom'
          maxLength={2}
        />
        <button onClick={incrementMinutes} type='button'>
          <FontAwesomeIcon icon={faAngleDown} />
        </button>
      </div>
    </div>
  );
};
